import { useEffect, useRef } from "react";
import { ShieldCheck, HeartPulse, GraduationCap, FileCheck } from "lucide-react";

const SAFETY_ITEMS = [
  { icon: GraduationCap, title: "Professionally Trained Facilitators", desc: "Led by certified domain experts trained for school-age students in active outdoor environments." },
  { icon: ShieldCheck, title: "Age-Appropriate Structuring", desc: "Activities calibrated to physical and cognitive capabilities of each grade level, Nursery to Grade 12." },
  { icon: HeartPulse, title: "First Aid & Emergency Readiness", desc: "On-site first aid stations, trained medical response personnel, and comprehensive emergency protocols." },
  { icon: FileCheck, title: "Insured & Fully Compliant", desc: "Full liability insurance, transportation compliance, and institutional indemnification." },
];

const KEY_POINTS = [
  "Strict 1:20 staff-to-student ratio",
  "Pre-visit campus safety walkthrough available",
  "Well-maintained, secure environments",
  "Parent communication materials provided",
];

export default function SafetySection() {
  const ref = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) entry.target.classList.add("visible"); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="safety" data-testid="safety-section" ref={ref} className="reveal-section py-16 md:py-20 bg-primary text-primary-foreground">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-accent mb-4">Safety & Infrastructure</p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-white mb-4">
            Your Students Are in Capable Hands
          </h2>
          <p className="text-base text-white/60 font-light max-w-2xl mx-auto">
            Safety is the foundation of every program-not just a checklist.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {SAFETY_ITEMS.map((item, i) => {
            const Icon = item.icon;
            return (
              <div key={i} data-testid={`safety-card-${i}`} className="p-6 border border-white/10 bg-white/5 hover:bg-white/10 transition-all duration-300">
                <Icon className="w-6 h-6 text-accent mb-4" />
                <h4 className="font-heading text-lg font-medium text-white mb-2">{item.title}</h4>
                <p className="text-sm text-white/60 font-light leading-relaxed">{item.desc}</p>
              </div>
            );
          })}
        </div>

        <div className="border-t border-white/10 pt-10">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {KEY_POINTS.map((point, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" />
                <span className="text-sm text-white/70 font-light">{point}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
